import {
	useMutation as useRqMutation,
	useQuery as useRqQuery,
} from "@tanstack/react-query";
import { CopyIcon, KeyIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { authClient, getActiveOrgId } from "@/lib/authClient";

type ApiKeyRow = {
	id: string;
	name: string | null;
	start: string | null;
	prefix: string | null;
	createdAt: number | string;
	lastRequest: number | string | null;
	expiresAt: number | string | null;
	enabled: boolean;
};

function formatDate(value: number | string | null): string {
	if (value === null || value === undefined || value === "") return "—";
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function maskedPreview(row: ApiKeyRow): string {
	if (row.prefix && row.start) return `${row.prefix}${row.start}••••`;
	if (row.start) return `${row.start}••••`;
	return "••••";
}

export default function ApiKeysPage() {
	const session = authClient.useSession();
	const userId = session.data?.user?.id;
	const activeOrganizationId = getActiveOrgId(session.data?.session);

	const keysQuery = useRqQuery({
		queryKey: ["apiKeys", userId, activeOrganizationId],
		queryFn: async () => {
			const result = await authClient.apiKey.list();
			if (result.error)
				throw new Error(result.error.message ?? "Failed to load keys");
			const data = result.data as unknown;
			if (Array.isArray(data)) return data as ApiKeyRow[];
			if (
				data &&
				typeof data === "object" &&
				"apiKeys" in data &&
				Array.isArray((data as { apiKeys: unknown }).apiKeys)
			) {
				return (data as { apiKeys: ApiKeyRow[] }).apiKeys;
			}
			return [];
		},
		enabled: Boolean(userId),
	});

	const [createOpen, setCreateOpen] = useState(false);
	const [revealedKey, setRevealedKey] = useState<{
		key: string;
		name: string;
	} | null>(null);
	const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);
	const [name, setName] = useState("");
	const [createError, setCreateError] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const createMutation = useRqMutation({
		mutationFn: async () => {
			if (!userId) throw new Error("Not signed in");
			if (!activeOrganizationId) {
				throw new Error(
					"Active organization not set; switch to an organization before creating a key.",
				);
			}
			const result = await authClient.apiKey.create({
				name: name.trim() || "Outlook add-in",
				expiresIn: null,
				metadata: { userId, organizationId: activeOrganizationId },
			});
			if (result.error)
				throw new Error(result.error.message ?? "Create failed");
			return result.data;
		},
		onSuccess: (data) => {
			const plaintext =
				data &&
				typeof data === "object" &&
				"key" in data &&
				typeof data.key === "string"
					? data.key
					: "";
			setRevealedKey({ key: plaintext, name: name.trim() || "Outlook add-in" });
			setCreateOpen(false);
			setName("");
			setCreateError(null);
			void keysQuery.refetch();
		},
		onError: (err) => {
			setCreateError(err instanceof Error ? err.message : String(err));
		},
	});

	const revokeMutation = useRqMutation({
		mutationFn: async (keyId: string) => {
			const result = await authClient.apiKey.delete({ keyId });
			if (result.error)
				throw new Error(result.error.message ?? "Revoke failed");
			return result.data;
		},
		onSettled: () => {
			setRevokeTarget(null);
			void keysQuery.refetch();
		},
	});

	async function copyKey() {
		if (!revealedKey) return;
		try {
			await navigator.clipboard.writeText(revealedKey.key);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// ignore
		}
	}

	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6">
				<div className="flex items-start justify-between gap-4">
					<div>
						<h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
							<KeyIcon className="size-5" />
							API keys
						</h1>
						<p className="mt-1 text-sm text-muted-foreground">
							Generate keys to sign in to the Outlook add-in. Each key is scoped
							to your current organization.
						</p>
					</div>
					<Button
						onClick={() => setCreateOpen(true)}
						disabled={!activeOrganizationId}
					>
						Create key
					</Button>
				</div>

				{!activeOrganizationId ? (
					<Alert>
						<AlertTitle>No active organization</AlertTitle>
						<AlertDescription>
							Switch to an organization from the sidebar before creating a key.
						</AlertDescription>
					</Alert>
				) : null}

				<Card>
					<CardHeader>
						<CardTitle className="text-base">Your keys</CardTitle>
					</CardHeader>
					<CardContent>
						{keysQuery.isLoading ? (
							<p className="text-sm text-muted-foreground">Loading…</p>
						) : keysQuery.data && keysQuery.data.length > 0 ? (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Name</TableHead>
										<TableHead>Key</TableHead>
										<TableHead>Created</TableHead>
										<TableHead>Last used</TableHead>
										<TableHead className="w-12" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{keysQuery.data.map((row) => (
										<TableRow key={row.id}>
											<TableCell className="font-medium">
												{row.name ?? "(unnamed)"}
											</TableCell>
											<TableCell className="font-mono text-xs text-muted-foreground">
												{maskedPreview(row)}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{formatDate(row.createdAt)}
											</TableCell>
											<TableCell className="text-muted-foreground">
												{formatDate(row.lastRequest)}
											</TableCell>
											<TableCell>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => setRevokeTarget(row)}
													title="Revoke"
												>
													<Trash2Icon className="size-4" />
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						) : (
							<p className="text-sm text-muted-foreground">
								No keys yet. Click “Create key” to generate one.
							</p>
						)}
					</CardContent>
				</Card>

				<Dialog open={createOpen} onOpenChange={setCreateOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Create API key</DialogTitle>
							<DialogDescription>
								The full key is shown once after creation. Store it somewhere
								safe.
							</DialogDescription>
						</DialogHeader>
						<div className="flex flex-col gap-2 py-2">
							<Label htmlFor="api-key-name">Name</Label>
							<Input
								id="api-key-name"
								placeholder="e.g. Outlook add-in (laptop)"
								value={name}
								onChange={(e) => setName(e.target.value)}
								autoFocus
							/>
							{createError ? (
								<p className="text-destructive text-xs">{createError}</p>
							) : null}
						</div>
						<DialogFooter>
							<Button variant="ghost" onClick={() => setCreateOpen(false)}>
								Cancel
							</Button>
							<Button
								onClick={() => createMutation.mutate()}
								disabled={createMutation.isPending}
							>
								{createMutation.isPending ? "Creating…" : "Create"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				<Dialog
					open={Boolean(revealedKey)}
					onOpenChange={(open) => {
						if (!open) setRevealedKey(null);
					}}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Copy your key</DialogTitle>
							<DialogDescription>
								This is the only time the full key is shown. If you lose it,
								revoke it and create a new one.
							</DialogDescription>
						</DialogHeader>
						<div className="flex flex-col gap-2 py-2">
							<Label className="text-xs uppercase tracking-wide text-muted-foreground">
								{revealedKey?.name}
							</Label>
							<div className="flex items-center gap-2 rounded-md border bg-muted/40 p-2 font-mono text-sm">
								<span className="flex-1 select-all break-all">
									{revealedKey?.key}
								</span>
								<Button variant="outline" size="sm" onClick={copyKey}>
									<CopyIcon className="size-4" />
									{copied ? "Copied" : "Copy"}
								</Button>
							</div>
						</div>
						<DialogFooter>
							<Button onClick={() => setRevealedKey(null)}>Done</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				<AlertDialog
					open={Boolean(revokeTarget)}
					onOpenChange={(open) => {
						if (!open) setRevokeTarget(null);
					}}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Revoke this key?</AlertDialogTitle>
							<AlertDialogDescription>
								Any add-in signed in with “{revokeTarget?.name ?? "this key"}”
								will be signed out on its next request.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => {
									if (revokeTarget) revokeMutation.mutate(revokeTarget.id);
								}}
							>
								Revoke
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</ScrollArea>
	);
}
